import React, { useState, useEffect } from "react";
import AuthenticatedImage from "../../../components/AuthenticatedImage";
import AuthenticatedAvatar from "../../../components/AuthenticatedAvatar";
import {
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  Spinner,
  Modal,
  Pagination,
  Card,
  Badge,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  PencilSquare,
  CheckCircleFill,
  XCircleFill,
  PlusCircle,
  LockFill,
  EyeFill,
  PeopleFill,
  Search,
  PersonBadge,
  GeoAltFill,
  EnvelopeFill,
  PersonVcardFill,
  PersonXFill,
  PersonPlusFill,
  ArrowRepeat,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Avatar,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddInstructorOffcanvas from "./AddInstructorOffcanvas";
import AddPrisonerOffcanvas from "./AddPrisonerOffcanvas";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const secondaryGradient = "linear-gradient(135deg, #00acc1, #00838f)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const AssignUsers = () => {
  const [instructors, setInstructors] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showAddInstructor, setShowAddInstructor] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [locationList, setLocationList] = useState([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState("");
  const [selectedInstructorLocation, setSelectedInstructorLocation] =
    useState("");
  const [locationObjects, setLocationObjects] = useState([]);
  const [showAddPrisonerOffcanvas, setShowAddPrisonerOffcanvas] =
    useState(false);

  // New state for user assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [assigningUsers, setAssigningUsers] = useState(false);

  // Pagination and filtering state
  const [totalInstructors, setTotalInstructors] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    location: "",
    status: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    locationId: "",
  });

  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Helper function to get location string
  const getLocationString = (location) => {
    if (!location) return "-";
    return typeof location === "object" ? location.location : location;
  };

  const fetchInstructors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");

      let statusFilter = "";
      if (filters.status === "active") {
        statusFilter = "1";
      } else if (filters.status === "inactive") {
        statusFilter = "2";
      } else if (filters.status === "not verified") {
        statusFilter = "0";
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: filters.search,
        filterLocation: filters.location,
        status: statusFilter,
      };

      const res = await axios.get(
        `${API_BASE_URL}/api/admin/getRegister/mentor`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        },
      );

      setInstructors(res.data.users || []);
      setTotalInstructors(res.data.total || 0);
    } catch (err) {
      console.error("Error fetching instructors:", err);
      toast.error("Failed to fetch instructors");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const locations = res.data?.data || [];
      const activeLocations = locations
        .filter((loc) => loc.status === "Active")
        .map((loc) => ({
          id: loc._id,
          name: loc.location,
        }));
      setLocationList(activeLocations);
    } catch (err) {
      console.error("Failed to fetch locations:", err);
      toast.error("Failed to load locations");
    }
  };

const fetchUnassignedUsers = async (instructor) => {
  try {
    setLoadingUnassigned(true);
    const token = localStorage.getItem("adminToken");

    const response = await axios.get(
      `${API_BASE_URL}/api/assignInstructor/getUsersDirectly/${instructor?._id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    // Filter to only show users where isAssignedToThisMentor is true
    const assignedUsers = (response.data.users || []).filter(
      (user) => user.isAssignedToThisMentor === true
    );
    
    setUnassignedUsers(assignedUsers);
    setSelectedUserIds(assignedUsers.filter((u) => u.isAssigned).map((u) => u._id));
  } catch (err) {
    console.error("Error fetching unassigned users:", err);
    toast.error("Failed to fetch unassigned users");
  } finally {
    setLoadingUnassigned(false);
  }
};

  const handleAssignUsers = async () => {
    if (!selectedInstructor || !selectedUserIds.length) {
      toast.error("Please select at least one user to assign");
      return;
    }

    try {
      setAssigningUsers(true);
      const token = localStorage.getItem("adminToken");

      await axios.post(
        `${API_BASE_URL}/api/assignInstructor/assignUser/${selectedInstructor._id}`,
        { userIds: selectedUserIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success(
        `Successfully assigned ${selectedUserIds.length} user(s) to instructor`,
      );
      setShowAssignModal(false);
      setSelectedUserIds([]);
      fetchInstructors();
    } catch (err) {
      console.error("Error assigning users:", err);
      toast.error("Failed to assign users");
    } finally {
      setAssigningUsers(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === unassignedUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(unassignedUsers.map((user) => user._id));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const toggleStatus = async (instructor) => {
    if (instructor.user_status === 0) {
      toast.error("❌ User email is not verified!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const newStatus = instructor.user_status === 1 ? 2 : 1;

      await axios.patch(
        `${API_BASE_URL}/api/admin/editUserStatus/${instructor._id}`,
        { user_status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      fetchInstructors();
      toast.success(
        `Status updated to ${newStatus === 1 ? "Active" : "Inactive"}`,
      );
    } catch (err) {
      console.error("Error toggling status:", err);
      toast.error("Failed to update status");
    }
  };

  const handleResetPassword = async () => {
    if (!selectedInstructor || !selectedInstructor._id) {
      console.error("No instructor selected for password reset.");
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");

      await axios.put(
        `${API_BASE_URL}/api/admin/changeUserPassword/${selectedInstructor._id}`,
        passwords,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Password updated successfully!");
      setPasswords({ newPassword: "", confirmPassword: "" });
      setShowResetModal(false);
    } catch (err) {
      console.error(
        "Password reset failed:",
        err.response?.data || err.message,
      );
      toast.error("Password reset failed!");
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedInstructor || !selectedInstructor._id) {
      toast.error("No instructor selected");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const formDataToSend = new FormData();

      formDataToSend.append("name", formData.name);
      formDataToSend.append("location", formData.locationId);

      await axios.put(
        `${API_BASE_URL}/api/auth/update-user/${selectedInstructor._id}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("Profile updated successfully!");
      setShowEditModal(false);
      fetchInstructors();
    } catch (err) {
      console.error("Error updating profile:", err);
      toast.error("Failed to update profile");
    }
  };

  const openAssignModal = (instructor) => {
    setSelectedInstructor(instructor);
    setSelectedUserIds([]);
    setShowAssignModal(true);
    fetchUnassignedUsers(instructor);
  };

  useEffect(() => {
    fetchInstructors();
    fetchLocations();
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    if (selectedInstructor) {
      setFormData({
        name: selectedInstructor.name || "",
        locationId: selectedInstructor.location?._id || "",
      });
    }
  }, [selectedInstructor]);

  const totalPages = Math.ceil(totalInstructors / itemsPerPage);
  console.log(unassignedUsers)

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Assign Users
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <PersonBadge size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Assign Users
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {totalInstructors} Assign Users
            </Typography>
          </Box>
        </Box>

        {/* <Box display="flex" gap={2}>
          <Button
            variant="info"
            onClick={() => navigate("/users")}
            className="d-flex align-items-center gap-2 fw-bold"
            style={{
              background: secondaryGradient,
              border: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              boxShadow: "0 4px 14px rgba(0,172,193,0.3)",
              color: "#fff",
            }}
          >
            <EyeFill size={16} /> View Students
          </Button>

          <Button
            onClick={() => setShowAddInstructor(true)}
            className="d-flex align-items-center gap-2 fw-bold"
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            <PlusCircle size={16} /> Add New Instructor
          </Button>
        </Box> */}
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by name or email..."
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </Box>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
            >
              <option value="">All Locations</option>
              {locationList.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="not verified">Not Verified</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Select
              style={fieldStyle}
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading instructors...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "Profile",
                  "Name",
                  "Email",
                  "Location",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {instructors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    No instructors found.
                  </td>
                </tr>
              ) : (
                instructors.map((instructor, index) => (
                  <tr
                    key={instructor._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td>
                      <AuthenticatedAvatar
                        src={instructor.profilePicture}
                        alt={instructor.name}
                        sx={{
                          width: 40,
                          height: 40,
                          border: "2px solid #e4eaf8",
                        }}
                      />
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {instructor.name || "-"}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {instructor.email || "-"}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {getLocationString(instructor.location)}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          instructor.user_status === 0
                            ? "bg-warning text-dark"
                            : instructor.user_status === 1
                              ? "bg-success"
                              : "bg-danger"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {instructor.user_status === 0
                          ? "Unverified"
                          : instructor.user_status === 1
                            ? "Active"
                            : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {/* <ActionBtn
                          tooltip={
                            instructor.user_status === 1
                              ? "Deactivate"
                              : "Activate"
                          }
                          color={
                            instructor.user_status === 1 ? "#c62828" : "#2e7d32"
                          }
                          disabled={instructor.user_status === 0}
                          onClick={() => toggleStatus(instructor)}
                        >
                          {instructor.user_status === 1 ? (
                            <XCircleFill size={17} />
                          ) : (
                            <CheckCircleFill size={17} />
                          )}
                        </ActionBtn> */}

                        {/* <ActionBtn
                          tooltip="Edit"
                          color="#f57f17"
                          onClick={() => {
                            setSelectedInstructor(instructor);
                            setShowEditModal(true);
                          }}
                        >
                          <PencilSquare size={16} />
                        </ActionBtn> */}

                        <ActionBtn
                          tooltip="View Profile"
                          color="#0d47a1"
                          onClick={() => {
                            setSelectedInstructor(instructor);
                            setShowProfileModal(true);
                            fetchUnassignedUsers(instructor);
                          }}
                        >
                          <EyeFill size={16} />
                        </ActionBtn>

                        {/* <ActionBtn
                          tooltip="Reset Password"
                          color="#6a1b9a"
                          onClick={() => {
                            setSelectedInstructor(instructor);
                            setShowResetModal(true);
                          }}
                        >
                          <LockFill size={16} />
                        </ActionBtn> */}

                        <ActionBtn
                          tooltip="Assign Users"
                          color="#2e7d32"
                          onClick={() => openAssignModal(instructor)}
                        >
                          <PersonPlusFill size={16} />
                        </ActionBtn>

                        {/* <ActionBtn
                          tooltip="Add users"
                          color="#e65100"
                          onClick={() => {
                            setSelectedInstructor(instructor);
                            setShowAddPrisonerOffcanvas(true);
                          }}
                        >
                          <PlusCircle size={16} />
                        </ActionBtn> */}
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {totalInstructors > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(currentPage - 1) * itemsPerPage + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(currentPage * itemsPerPage, totalInstructors)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{totalInstructors}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === currentPage
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      {/* <AddInstructorOffcanvas
        show={showAddInstructor}
        handleClose={() => setShowAddInstructor(false)}
        onInstructorAdded={fetchInstructors}
      /> */}

      {/* <AddPrisonerOffcanvas
        show={showAddPrisonerOffcanvas}
        handleClose={() => {
          setShowAddPrisonerOffcanvas(false);
          setSelectedInstructor(null);
        }}
        instructorId={selectedInstructor?._id || ""}
        locations={locationList}
        preSelectedLocation={
          selectedInstructor?.location?._id ||
          selectedInstructor?.location ||
          ""
        }
      /> */}

      {/* Assign Users Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => {
          setShowAssignModal(false);
          setSelectedUserIds([]);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PeopleFill size={18} /> Assign Users to {selectedInstructor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {loadingUnassigned ? (
            <Box display="flex" justifyContent="center" py={5}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
            </Box>
          ) : unassignedUsers.length === 0 ? (
            <Box textAlign="center" py={5}>
              <Typography sx={{ color: "#7b8ab8" }}>
                No unassigned users available
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                mb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#1a237e", fontWeight: 600 }}
                >
                  Select users to assign:
                </Typography>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSelectAll}
                  style={{ color: "#0d47a1", textDecoration: "none" }}
                >
                  {selectedUserIds.length === unassignedUsers.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
              </Box>
              <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table bordered hover size="sm">
                  <thead style={{ background: "#e8ecf4" }}>
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Checkbox
                          checked={
                            selectedUserIds.length === unassignedUsers.length &&
                            unassignedUsers.length > 0
                          }
                          indeterminate={
                            selectedUserIds.length > 0 &&
                            selectedUserIds.length < unassignedUsers.length
                          }
                          onChange={handleSelectAll}
                          sx={{ padding: 0 }}
                        />
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unassignedUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                        <Checkbox
  checked={selectedUserIds.includes(user._id)} // ✅ single source of truth
  onChange={() => handleSelectUser(user._id)}
  sx={{ padding: 0 }}
/>
                        </td>
                        <td style={{ fontWeight: 500 }}>{user.name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>
                          <Badge bg="secondary" className="px-2 py-1">
                            {user.role || "User"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                  Selected: {selectedUserIds.length} user(s)
                </Typography>
              </Box>
            </>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAssignModal(false);
              setSelectedUserIds([]);
            }}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAssignUsers}
            disabled={assigningUsers || selectedUserIds.length === 0}
            style={{ borderRadius: "8px" }}
          >
            {assigningUsers ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Assigning...
              </>
            ) : (
              <>
                <PersonPlusFill size={14} className="me-2" />
                Assign Selected Users
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Profile View Modal */}
      <Modal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PersonVcardFill size={18} /> Instructor Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedInstructor ? (
            <>
              <Box className="d-flex flex-row gap-2">
                <Box
                  sx={{
                    background: primaryGradient,
                    borderRadius: "10px",
                    p: 3,
                    textAlign: "center",
                    mb: 2,
                    width: "100%",
                  }}
                >
                  <AuthenticatedImage
                    src={
                      selectedInstructor.profilePicture ||
                      "https://via.placeholder.com/80"
                    }
                    alt="Profile"
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid rgba(255,255,255,0.4)",
                    }}
                  />
                  <Typography sx={{ color: "#fff", fontWeight: 700, mt: 1 }}>
                    {selectedInstructor.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    Instructor
                  </Typography>
                </Box>
                <Box className="d-flex flex-column gap-1">
                  {[
                    ["Email", selectedInstructor.email],
                    [
                      "Location",
                      getLocationString(selectedInstructor.location),
                    ],
                  ].map(([k, v]) => (
                    <Box
                      key={k}
                      display="flex"
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 1.2,
                        borderRadius: "8px",
                        mb: 0.5,
                        background: "#fff",
                        border: "1px solid #e8ecf4",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#7b8ab8",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          width: 120,
                          flexShrink: 0,
                          
                        }}
                      >
                        {k}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.84rem",
                          color: "#1a237e",
                          fontWeight: 500,
                        
                        }}
                        style={{  marginBottom: 0}}
                      >
                        {v}
                      </Typography>
                    </Box>
                  ))}

                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: "8px",
                      background: "#fff",
                      border: "1px solid #e8ecf4",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#7b8ab8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        width: 120,
                        flexShrink: 0,
                      }}
                    >
                      Status
                    </Typography>
                    <span
                      className={`badge px-3 py-2 rounded-pill ${
                        selectedInstructor.user_status === 0
                          ? "bg-warning text-dark"
                          : selectedInstructor.user_status === 1
                            ? "bg-success"
                            : "bg-danger"
                      }`}
                    >
                      {selectedInstructor.user_status === 0
                        ? "Unverified"
                        : selectedInstructor.user_status === 1
                          ? "Active"
                          : "Inactive"}
                    </span>
                  </Box>
                </Box>
              </Box>

              {/* Assigned Users Section */}
              <Box mt={3}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PeopleFill size={16} /> Assigned Users
                </Typography>

                {unassignedUsers.filter((user) => user.isAssigned == true)
                  .length > 0 ? (
                  <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Table bordered hover size="sm">
                      <thead style={{ background: "#e8ecf4" }}>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unassignedUsers
                          
                          .map((user) => (
                            <tr key={user._id}>
                              <td style={{ fontWeight: 500 }}>
                                {user.name || "-"}
                              </td>
                              <td>{user.email || "-"}</td>
                              <td>
                                <Badge
                                  bg={user.isAssigned ? "success"  : "secondary"}
                                  className="px-2 py-1"
                                >
                                  {user.isAssigned ? "Assigned" : "Unassigned"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: "8px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid #e8ecf4",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                      No users assigned to this instructor
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Typography sx={{ color: "#7b8ab8", textAlign: "center", py: 3 }}>
              Loading instructor data...
            </Typography>
          )}
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      {/* <Modal
        show={showResetModal}
        onHide={() => {
          setShowResetModal(false);
          setPasswords({ newPassword: "", confirmPassword: "" });
        }}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <LockFill size={18} /> Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>New Password</Form.Label>
              <Form.Control
                style={fieldStyle}
                type="password"
                value={passwords.newPassword}
                onChange={(e) =>
                  setPasswords({ ...passwords, newPassword: e.target.value })
                }
                placeholder="Enter new password"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Confirm Password</Form.Label>
              <Form.Control
                style={fieldStyle}
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    confirmPassword: e.target.value,
                  })
                }
                placeholder="Confirm new password"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowResetModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleResetPassword}
            style={{ borderRadius: "8px" }}
          >
            Update Password
          </Button>
        </Modal.Footer>
      </Modal> */}

      {/* Profile View Modal */}
      {/* <Modal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PersonVcardFill size={18} /> Instructor Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedInstructor ? (
            <>
              <Box
                sx={{
                  background: primaryGradient,
                  borderRadius: "10px",
                  p: 3,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                <AuthenticatedImage
                  src={
                    selectedInstructor.profilePicture ||
                    "https://via.placeholder.com/80"
                  }
                  alt="Profile"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid rgba(255,255,255,0.4)",
                  }}
                />
                <Typography sx={{ color: "#fff", fontWeight: 700, mt: 1 }}>
                  {selectedInstructor.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  Instructor
                </Typography>
              </Box>

              {[
                ["Email", selectedInstructor.email],
                ["Location", getLocationString(selectedInstructor.location)],
              ].map(([k, v]) => (
                <Box
                  key={k}
                  display="flex"
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: "8px",
                    mb: 0.5,
                    background: "#fff",
                    border: "1px solid #e8ecf4",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#7b8ab8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: 120,
                      flexShrink: 0,
                    }}
                  >
                    {k}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.84rem",
                      color: "#1a237e",
                      fontWeight: 500,
                    }}
                  >
                    {v}
                  </Typography>
                </Box>
              ))}

              <Box
                display="flex"
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #e8ecf4",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#7b8ab8",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    width: 120,
                    flexShrink: 0,
                  }}
                >
                  Status
                </Typography>
                <span
                  className={`badge px-3 py-2 rounded-pill ${
                    selectedInstructor.user_status === 0
                      ? "bg-warning text-dark"
                      : selectedInstructor.user_status === 1
                        ? "bg-success"
                        : "bg-danger"
                  }`}
                >
                  {selectedInstructor.user_status === 0
                    ? "Unverified"
                    : selectedInstructor.user_status === 1
                      ? "Active"
                      : "Inactive"}
                </span>
              </Box>
            </>
          ) : (
            <Typography sx={{ color: "#7b8ab8", textAlign: "center", py: 3 }}>
              Loading instructor data...
            </Typography>
          )}
        </Modal.Body>
      </Modal> */}

      {/* Edit Modal */}
      {/* <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PencilSquare size={18} /> Edit Instructor Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedInstructor && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Name</Form.Label>
                <Form.Control
                  style={fieldStyle}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Location</Form.Label>
                <Form.Select
                  style={fieldStyle}
                  value={formData.locationId}
                  onChange={(e) =>
                    setFormData({ ...formData, locationId: e.target.value })
                  }
                >
                  <option value="">Select a location</option>
                  {locationList.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "8px",
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal> */}
    </Box>
  );
};

export default AssignUsers;
